import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Bell, Layout, Plus, Trash2, Settings, Play, Eye,
  MessageCircle, DollarSign, UserPlus, Users, Volume2, Palette
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StreamOverlays = ({ onClose }) => {
  const [overlays, setOverlays] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('overlays'); // 'overlays' or 'alerts'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverlays();
    fetchAlertSettings();
  }, []);

  const fetchOverlays = async () => {
    try {
      const response = await axios.get(`${API}/overlays`);
      setOverlays(response.data || []);
    } catch (error) {
      console.error('Error fetching overlays:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const response = await axios.get(`${API}/alerts/settings`);
      setAlertSettings(response.data);
    } catch (error) {
      console.error('Error fetching alert settings:', error);
    }
  };

  const handleDeleteOverlay = async (overlayId) => {
    if (!window.confirm('Delete this overlay?')) return;
    try {
      await axios.delete(`${API}/overlays/${overlayId}`);
      fetchOverlays();
    } catch (error) {
      console.error('Error deleting overlay:', error);
    }
  };

  const handleToggleOverlay = async (overlayId, enabled) => {
    try {
      await axios.put(`${API}/overlays/${overlayId}`, null, {
        params: { enabled: !enabled }
      });
      fetchOverlays();
    } catch (error) {
      console.error('Error toggling overlay:', error);
    }
  };

  const handleTestAlert = async (alertType) => {
    try {
      await axios.post(`${API}/alerts/test`, null, {
        params: { alert_type: alertType }
      });
      alert('Test alert sent! Check your stream.');
    } catch (error) {
      console.error('Error testing alert:', error);
    }
  };

  const handleUpdateAlertSetting = async (alertType, setting, value) => {
    const newSettings = {
      ...alertSettings,
      [alertType]: {
        ...alertSettings[alertType],
        [setting]: value
      }
    };
    
    try {
      await axios.put(`${API}/alerts/settings`, null, {
        params: { settings: JSON.stringify(newSettings) }
      });
      setAlertSettings(newSettings);
    } catch (error) {
      console.error('Error updating alert settings:', error);
    }
  };

  const overlayTypes = [
    { id: 'alert', name: 'Alert Box', icon: Bell, description: 'Show follower, tip, and sub alerts' },
    { id: 'chat', name: 'Chat Overlay', icon: MessageCircle, description: 'Display live chat on stream' },
    { id: 'goals', name: 'Stream Goals', icon: Users, description: 'Follower/sub/tip goals' },
    { id: 'recent_events', name: 'Recent Events', icon: Layout, description: 'Show recent followers, tips' },
    { id: 'custom', name: 'Custom Widget', icon: Palette, description: 'Create custom overlay' }
  ];

  const alertTypes = [
    { id: 'follower', name: 'New Follower', icon: UserPlus, color: '#3B82F6' },
    { id: 'tip', name: 'Tip/Donation', icon: DollarSign, color: '#10B981' },
    { id: 'subscription', name: 'Subscription', icon: Users, color: '#8B5CF6' },
    { id: 'raid', name: 'Raid', icon: Users, color: '#F59E0B' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="stream-overlays"
      >
        {/* Header */}
        <div className="p-6 border-b" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <Layout className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ color: '#111111' }}>Stream Overlays & Alerts</h2>
                <p className="text-sm text-gray-500">Customize your stream experience</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5">
              <X className="w-5 h-5" style={{ color: '#6B7280' }} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('overlays')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'overlays' 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'hover:bg-black/5 text-gray-600'
              }`}
            >
              <Layout className="w-4 h-4" />
              Overlays
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'alerts' 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'hover:bg-black/5 text-gray-600'
              }`}
            >
              <Bell className="w-4 h-4" />
              Alerts
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overlays' ? (
            <div className="space-y-6">
              {/* Create New Overlay */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-3">CREATE OVERLAY</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {overlayTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setShowCreateModal(type)}
                      className="p-4 rounded-xl border-2 border-dashed hover:border-orange-300 hover:bg-orange-50 transition-colors text-left"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      <type.icon className="w-6 h-6 text-orange-500 mb-2" />
                      <p className="font-medium text-sm" style={{ color: '#111111' }}>{type.name}</p>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* My Overlays */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-3">MY OVERLAYS</h3>
                {overlays.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl">
                    <Layout className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No overlays created yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {overlays.map((overlay) => (
                      <div key={overlay.id} className="p-4 rounded-xl bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${overlay.enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                            <Layout className={`w-5 h-5 ${overlay.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{overlay.name}</p>
                            <p className="text-xs text-gray-500 capitalize">{overlay.type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleOverlay(overlay.id, overlay.enabled)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                              overlay.enabled 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {overlay.enabled ? 'Active' : 'Inactive'}
                          </button>
                          <button
                            onClick={() => handleDeleteOverlay(overlay.id)}
                            className="p-2 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Alert Settings */}
              {alertTypes.map((alertType) => (
                <div key={alertType.id} className="p-4 rounded-xl border" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${alertType.color}20` }}>
                        <alertType.icon className="w-5 h-5" style={{ color: alertType.color }} />
                      </div>
                      <div>
                        <p className="font-medium">{alertType.name}</p>
                        <p className="text-xs text-gray-500">Customize how this alert appears</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleTestAlert(alertType.id)}
                        className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" />
                        Test
                      </button>
                      {alertSettings && (
                        <button
                          onClick={() => handleUpdateAlertSetting(alertType.id, 'enabled', !alertSettings[alertType.id]?.enabled)}
                          className={`w-12 h-6 rounded-full transition-colors relative ${
                            alertSettings[alertType.id]?.enabled ? 'bg-green-500' : 'bg-gray-200'
                          }`}
                        >
                          <div 
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              alertSettings[alertType.id]?.enabled ? 'translate-x-7' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  </div>

                  {alertSettings && alertSettings[alertType.id]?.enabled && (
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Animation</label>
                        <select
                          value={alertSettings[alertType.id]?.animation || 'slide'}
                          onChange={(e) => handleUpdateAlertSetting(alertType.id, 'animation', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E5E7EB' }}
                        >
                          <option value="slide">Slide</option>
                          <option value="bounce">Bounce</option>
                          <option value="fade">Fade</option>
                          <option value="confetti">Confetti</option>
                          <option value="flash">Flash</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Sound</label>
                        <select
                          value={alertSettings[alertType.id]?.sound || 'default'}
                          onChange={(e) => handleUpdateAlertSetting(alertType.id, 'sound', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E5E7EB' }}
                        >
                          <option value="default">Default</option>
                          <option value="coins">Coins</option>
                          <option value="celebration">Celebration</option>
                          <option value="horn">Horn</option>
                          <option value="none">None</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Duration (sec)</label>
                        <input
                          type="number"
                          value={alertSettings[alertType.id]?.duration || 5}
                          onChange={(e) => handleUpdateAlertSetting(alertType.id, 'duration', parseInt(e.target.value))}
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E5E7EB' }}
                          min={1}
                          max={30}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Create Overlay Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateOverlayModal 
            type={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              fetchOverlays();
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CreateOverlayModal = ({ type, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/overlays`, null, {
        params: {
          name,
          overlay_type: type.id,
          settings: JSON.stringify({})
        }
      });
      onCreated();
    } catch (error) {
      console.error('Error creating overlay:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
      >
        <h3 className="text-lg font-bold mb-4">Create {type.name}</h3>
        
        <div className="mb-4">
          <label className="text-sm text-gray-500 block mb-1">Overlay Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`My ${type.name}`}
            className="w-full px-4 py-3 rounded-xl border outline-none focus:border-orange-500"
            style={{ borderColor: '#E5E7EB' }}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border font-medium"
            style={{ borderColor: '#E5E7EB' }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={loading || !name.trim()}
            className="flex-1 py-3 rounded-xl bg-orange-500 text-white font-medium disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StreamOverlays;
